import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Button, Row, Col, Card, Badge, Alert, Spinner,
  ListGroup, ProgressBar, Dropdown, Collapse
} from 'react-bootstrap';
import {
  User, Phone, Calendar, Clock, DollarSign, Shirt, Ruler,
  Package, FileText, Plus, Trash2, XCircle,
  Edit, Eye, Printer, Scissors, X, Image as ImageIcon,
  ChevronDown, ChevronUp,
  Upload
} from 'lucide-react';

import customOrderService from '../../../services/customOrderService';
import modelService from '../../../services/modelService'; // ✅ NOUVEAU
import measurementService from '../../../services/measurementService';
import OrderStatusBadge from './OrderStatusBadge';
import OrderStatusTimeline from './OrderStatusTimeline';
import AddPaymentModal from './AddPaymentModal';

import OrderTicketPrintService from '../../../services/OrderTicketPrintService';
import { useBoutique } from '../../../contexts/BoutiqueContext';

/**
 * ✅ ATELIER: Modal de détail d'une commande avec :
 *  - Informations complètes
 *  - Timeline du statut
 *  - Liste des paiements
 *  - Mesures détaillées (toggle + grille 3 colonnes)
 *  - Actions : changer statut, ajouter paiement, annuler, imprimer ticket
 *
 * ✅ NOUVEAU: Affichage des 2 images (modèle + tissu) côte à côte avec zoom
 */
const OrderDetailModal = ({ show, onHide, order, onOrderUpdated, isManager }) => {

  // ============================================================
  // ============== HOOKS (TOUS AVANT RETURN NULL) ==============
  // ============================================================

  const { currentBoutique } = useBoutique();

  // États principaux
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [printing, setPrinting] = useState(false);

  // Sous-modals
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Zoom image plein écran
  const [zoomedImage, setZoomedImage] = useState(null);

  // ✅ États mesures (toggle + données)
  const [measurementDetails, setMeasurementDetails] = useState(null);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);

  const [uploadingFabric, setUploadingFabric] = useState(false);
  const fabricInputRef = useRef(null);

  // ✅ Effect : charger les mesures quand on ouvre une commande qui en a une
  useEffect(() => {
    if (show && order?.measurementId) {
      loadMeasurementDetails(order.measurementId);
    } else {
      setMeasurementDetails(null);
      setShowMeasurements(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, order?.measurementId]);

  // ✅ Return null APRÈS tous les hooks
  if (!order) return null;

  // ============================================================
  // ============== VARIABLES DÉRIVÉES ==========================
  // ============================================================

  const availableStatuses = customOrderService.getAvailableNextStatuses(order.status);
  const nextNormalStatuses = availableStatuses.filter(s => s !== 'CANCELLED');
  const canCancel = availableStatuses.includes('CANCELLED');
  const canAddPayment = !['CANCELLED'].includes(order.status) && !order.isFullyPaid;

  // URLs absolues des images modèle et tissu
  // ✅ MULTI-MODÈLES / MULTI-TISSUS
  const orderModels = customOrderService.getOrderModels(order);
  const fabricImageUrlsAbs = customOrderService.getFabricImageAbsoluteUrls(order);

  // URLs brutes (non résolues) alignées sur les absolues, pour la suppression
  const fabricImageUrlsRaw =
    Array.isArray(order.fabricImageUrls) && order.fabricImageUrls.length > 0
      ? order.fabricImageUrls
      : (order.fabricImageUrl ? [order.fabricImageUrl] : []);

  const canEditFabric = isManager
    && order.status !== 'CANCELLED'
    && order.status !== 'DELIVERED';
  // ============================================================
  // ============== CHARGEMENT MESURES ==========================
  // ============================================================

  const loadMeasurementDetails = async (measurementId) => {
    setLoadingMeasurements(true);
    try {
      const response = await measurementService.getMeasurementById(measurementId);
      if (response.success) {
        setMeasurementDetails(response.data);
      } else {
        console.warn('⚠️ Échec chargement mesures:', response.message);
        setMeasurementDetails(null);
      }
    } catch (err) {
      console.error('❌ Erreur chargement mesures:', err);
      setMeasurementDetails(null);
    } finally {
      setLoadingMeasurements(false);
    }
  };

  /**
   * Construire la liste des mesures à afficher (standards + custom)
   */
  const getMeasurementsList = () => {
    if (!measurementDetails) return [];

    const standardLabels = {
      tourPoitrine: 'Tour poitrine',
      tourTaille: 'Tour taille',
      tourHanches: 'Tour hanches',
      tourBassin: 'Tour bassin',
      longueurRobe: 'Longueur robe',
      longueurManche: 'Longueur manche',
      longueurJupe: 'Longueur jupe',
      tourBras: 'Tour bras',
      tourCuisse: 'Tour cuisse',
      tourCou: 'Tour cou',
      carrureDos: 'Carrure dos',
      hauteurEpaule: 'Hauteur épaule'
    };

    const list = [];

    // Mesures standards
    Object.entries(standardLabels).forEach(([key, label]) => {
      const value = measurementDetails[key];
      if (value !== null && value !== undefined && value !== '') {
        list.push({ label, value: `${value} cm`, isCustom: false });
      }
    });

    // Mesures personnalisées
    if (measurementDetails.customMeasurements
      && typeof measurementDetails.customMeasurements === 'object') {
      Object.entries(measurementDetails.customMeasurements).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          list.push({ label: key, value: `${value} cm`, isCustom: true });
        }
      });
    }

    return list;
  };

  // ============================================================
  // ============== HANDLERS ===================================
  // ============================================================

  const handleChangeStatus = async (newStatus) => {
    setLoading(true);
    setError(null);

    try {
      const response = await customOrderService.changeStatus(order.id, newStatus);

      if (response.success) {
        setSuccess(`Statut mis à jour : ${customOrderService.getStatusInfo(newStatus).label}`);
        setTimeout(() => setSuccess(null), 3000);
        onOrderUpdated?.(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      setError('Veuillez saisir une raison d\'annulation');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await customOrderService.changeStatus(
        order.id, 'CANCELLED', cancelReason.trim()
      );

      if (response.success) {
        setSuccess('Commande annulée');
        setShowCancelConfirm(false);
        setCancelReason('');
        onOrderUpdated?.(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (updatedOrder) => {
    setSuccess('Paiement enregistré avec succès');
    setTimeout(() => setSuccess(null), 3000);
    onOrderUpdated?.(updatedOrder);
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Supprimer ce paiement ? Cette action est irréversible.')) return;

    setLoading(true);
    try {
      const response = await customOrderService.deletePayment(paymentId);
      if (response.success) {
        setSuccess('Paiement supprimé');
        setTimeout(() => setSuccess(null), 3000);
        onOrderUpdated?.(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // ============== IMPRESSION TICKET ==========================
  // ============================================================

  const getBoutiqueInfoForPrint = () => ({
    name: currentBoutique?.name || 'Ma Boutique',
    address: currentBoutique?.address || 'Dakar, Sénégal',
    phone: currentBoutique?.phone || '+221 XX XXX XX XX',
    phone1: currentBoutique?.phone1 || '',
    email: currentBoutique?.email || null,
    logoUrl: currentBoutique?.logoUrl || currentBoutique?.logo,
    logo: currentBoutique?.logo || currentBoutique?.logoUrl,
    primaryColor: currentBoutique?.primaryColor || '#3b82f6',
    secondaryColor: currentBoutique?.secondaryColor || '#1d4ed8'
  });

  const handlePrintTicket = async () => {
    if (!order) return;
    try {
      setPrinting(true);
      setError(null);

      const result = await OrderTicketPrintService.printOrderTicket(
        order,
        getBoutiqueInfoForPrint(),
        { name: order.sellerName || 'Vendeur', id: order.sellerId }
      );

      if (result.success) {
        setSuccess('Ticket envoyé à l\'impression');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'impression');
    } finally {
      setPrinting(false);
    }
  };

  const handlePreviewTicket = async () => {
    if (!order) return;
    try {
      setError(null);
      const result = await OrderTicketPrintService.previewOrderTicket(
        order,
        getBoutiqueInfoForPrint(),
        { name: order.sellerName || 'Vendeur', id: order.sellerId }
      );

      if (!result.success) {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'aperçu');
    }
  };

  /**
   * Recharge la commande depuis le serveur et la renvoie au parent.
   * ⚠️ Ne JAMAIS appeler onOrderUpdated() sans argument : le parent fait
   * setSelectedOrder(undefined) et le modal se vide.
   */
  const refreshOrder = async () => {
    try {
      const res = await customOrderService.getOrderById(order.id);
      if (res.success && res.data) {
        onOrderUpdated?.(res.data);
      } else {
        console.warn('⚠️ Rechargement commande échoué:', res.message);
      }
    } catch (err) {
      console.error('❌ Erreur rechargement commande:', err);
    }
  };

  // ✅ MULTI-TISSUS : ajout d'une ou plusieurs photos
  const handleUploadFabric = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingFabric(true);
    setError(null);

    try {
      const res = await customOrderService.uploadFabricImages(order.id, files);

      if (res.urls.length > 0) {
        setSuccess(res.message);
        setTimeout(() => setSuccess(null), 3000);
        await refreshOrder();
      }
      if (!res.success) {
        setError(`${res.failed} photo(s) n'ont pas pu être envoyées`);
      }
    } catch (err) {
      setError('Erreur lors de l\'upload');
    } finally {
      setUploadingFabric(false);
      if (fabricInputRef.current) fabricInputRef.current.value = '';
    }
  };

  // ✅ MULTI-TISSUS : suppression d'UNE photo
  const handleDeleteFabric = async (rawUrl) => {
    if (!window.confirm('Supprimer cette photo de tissu ?')) return;

    setUploadingFabric(true);
    setError(null);

    try {
      const res = await customOrderService.deleteFabricImage(order.id, rawUrl);
      if (res.success) {
        setSuccess('Photo du tissu supprimée');
        setTimeout(() => setSuccess(null), 3000);
        await refreshOrder();
      } else {
        setError(res.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
    } finally {
      setUploadingFabric(false);
    }
  };

  // ============================================================
  // ============== RENDU =======================================
  // ============================================================

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="h5 mb-0">{order.orderNumber}</div>
                <small className="text-muted">
                  Créée le {customOrderService.formatDateTime(order.createdAt)}
                  {' — par '}
                  {order.sellerName}
                </small>
              </div>
              <div className="d-flex gap-2 align-items-center">
                {order.priority === 'URGENT' && (
                  <Badge bg="warning" text="dark">🔴 Urgent</Badge>
                )}
                <OrderStatusBadge status={order.status} />
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Alertes */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Timeline du statut */}
          {order.status !== 'CANCELLED' && (
            <Card className="mb-3">
              <Card.Body className="py-2">
                <OrderStatusTimeline currentStatus={order.status} />
              </Card.Body>
            </Card>
          )}

          {/* Bloc annulation si applicable */}
          {order.status === 'CANCELLED' && (
            <Alert variant="danger">
              <div className="fw-bold mb-1">❌ Commande annulée</div>
              {order.cancelledAt && (
                <small className="d-block">
                  Le {customOrderService.formatDateTime(order.cancelledAt)}
                  {order.cancelledByName ? ` par ${order.cancelledByName}` : ''}
                </small>
              )}
              {order.cancellationReason && (
                <small className="d-block mt-1">
                  <strong>Raison :</strong> {order.cancellationReason}
                </small>
              )}
            </Alert>
          )}

          <Row className="g-3">
            {/* Cliente */}
            <Col md={12}>
              <Card>
                <Card.Header className="py-2 bg-light">
                  <strong className="d-flex align-items-center gap-2">
                    <User size={16} />
                    Cliente
                  </strong>
                </Card.Header>
                <Card.Body>
                  <div className="fw-semibold">{order.customerName}</div>
                  {order.customerPhone && (
                    <div className="small text-muted d-flex align-items-center gap-1 mt-1">
                      <Phone size={12} />
                      {order.customerPhone}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* ✅ Mesures (header entier cliquable + grille 3 colonnes) */}
            <Col md={12}>
              <Card>
                <Card.Header
                  className="py-2 bg-light d-flex justify-content-between align-items-center"
                  style={{ cursor: order.measurementId ? 'pointer' : 'default' }}
                  onClick={() => order.measurementId && setShowMeasurements(!showMeasurements)}
                >
                  <strong className="d-flex align-items-center gap-2">
                    <Ruler size={16} />
                    Mesures
                    {order.measurementId && (
                      <Badge bg="info" pill className="ms-2">
                        {getMeasurementsList().length || order.measurementsCount || 0} mesure{(getMeasurementsList().length || order.measurementsCount || 0) > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </strong>

                  {order.measurementId && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMeasurements(!showMeasurements);
                      }}
                    >
                      {showMeasurements ? (
                        <>
                          <ChevronUp size={16} className="me-1" />
                          <small>Réduire</small>
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} className="me-1" />
                          <small>Voir détails</small>
                        </>
                      )}
                    </Button>
                  )}
                </Card.Header>

                <Card.Body className={!order.measurementId || !showMeasurements ? 'py-2' : ''}>
                  {!order.measurementId ? (
                    <small className="text-muted">Aucune fiche attachée à cette commande</small>
                  ) : !showMeasurements ? (
                    <small className="text-muted">
                      Cliquez sur "Voir détails" pour afficher les mesures
                    </small>
                  ) : (
                    <Collapse in={showMeasurements}>
                      <div>
                        {loadingMeasurements ? (
                          <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                            <small className="text-muted ms-2">Chargement des mesures...</small>
                          </div>
                        ) : measurementDetails ? (
                          <>
                            {/* Date de la fiche */}
                            <div className="small text-muted mb-2">
                              Fiche créée le {customOrderService.formatDate(measurementDetails.createdAt)}
                            </div>

                            {/* Liste des mesures en grille */}
                            <Row className="g-2">
                              {getMeasurementsList().map((m, idx) => (
                                <Col xs={6} md={4} key={idx}>
                                  <div className="border rounded p-2 bg-white">
                                    <div className="small text-muted d-flex align-items-center gap-1">
                                      {m.label}
                                      {m.isCustom && (
                                        <Badge bg="warning" text="dark" pill style={{ fontSize: '0.6rem' }}>
                                          Custom
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="fw-semibold">{m.value}</div>
                                  </div>
                                </Col>
                              ))}
                            </Row>

                            {/* Notes de la fiche si présentes */}
                            {measurementDetails.notes && (
                              <Alert variant="light" className="mt-3 mb-0 small">
                                <strong>Notes mesures :</strong> {measurementDetails.notes}
                              </Alert>
                            )}
                          </>
                        ) : (
                          <small className="text-warning">
                            Impossible de charger les détails des mesures
                          </small>
                        )}
                      </div>
                    </Collapse>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* ✅ MULTI-MODÈLES */}
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="py-2 bg-light">
                  <strong className="d-flex align-items-center gap-2">
                    <Shirt size={16} className="text-primary" />
                    Modèle{orderModels.length > 1 ? 's' : ''}
                    {orderModels.length > 1 && (
                      <Badge bg="primary">{orderModels.length}</Badge>
                    )}
                  </strong>
                </Card.Header>
                <Card.Body>
                  {orderModels.length === 0 ? (
                    <small className="text-muted">Aucun modèle</small>
                  ) : orderModels.map((m, index) => (
                    <div
                      key={m.id || `custom-${index}`}
                      className={index > 0 ? 'pt-2 mt-2 border-top' : ''}
                    >
                      <div className="fw-semibold">{m.name}</div>
                      <small className="text-muted d-block">
                        {m.isCustom
                          ? 'Modèle personnalisé (hors catalogue)'
                          : `Depuis le catalogue${m.category ? ` — ${m.category}` : ''}`}
                      </small>

                      <div className="mt-2">
                        {m.imageUrl ? (
                          <div
                            className="order-image-frame"
                            onClick={() => setZoomedImage({
                              url: m.imageUrl,
                              label: 'Modèle'
                            })}
                          >
                            <img
                              src={m.imageUrl}
                              alt={m.name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                            <div className="order-image-zoom-hint">
                              <ImageIcon size={12} />
                            </div>
                          </div>
                        ) : (
                          <div className="order-image-empty">
                            <ImageIcon size={28} />
                            <small className="mt-1">Aucune photo</small>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            {/* ✅ Tissu (description + image du tissu si modèle catalogue) */}
            {/* ✅ MULTI-TISSUS */}
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="py-2 bg-light">
                  <strong className="d-flex align-items-center gap-2">
                    <Scissors size={16} className="text-success" />
                    Tissu{fabricImageUrlsAbs.length > 1 ? 's' : ''}
                    {fabricImageUrlsAbs.length > 1 && (
                      <Badge bg="success">{fabricImageUrlsAbs.length}</Badge>
                    )}
                  </strong>
                </Card.Header>
                <Card.Body>
                  {order.fabricDescription ? (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{order.fabricDescription}</div>
                  ) : (
                    <small className="text-muted">Aucune description</small>
                  )}
                  <div className="mt-2 mb-2">
                    <Badge bg={order.fabricProvidedByCustomer ? 'info' : 'secondary'}>
                      {order.fabricProvidedByCustomer
                        ? '👗 Fourni par la cliente'
                        : '🏪 Fourni par l\'atelier'}
                    </Badge>
                  </div>

                  {/* Galerie des photos de tissu */}
                  <div className="mt-2">
                    {fabricImageUrlsAbs.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {fabricImageUrlsAbs.map((absUrl, index) => (
                          <div key={absUrl} className="position-relative">
                            <div
                              className="order-image-frame"
                              style={{ width: '96px', height: '96px' }}
                              onClick={() => setZoomedImage({
                                url: absUrl,
                                label: 'Tissu'
                              })}
                            >
                              <img
                                src={absUrl}
                                alt={`Tissu ${index + 1}`}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                              <div className="order-image-zoom-hint">
                                <ImageIcon size={12} />
                              </div>
                            </div>

                            {canEditFabric && (
                              <Button
                                variant="danger"
                                size="sm"
                                className="position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  transform: 'translate(30%, -30%)'
                                }}
                                onClick={() => handleDeleteFabric(fabricImageUrlsRaw[index])}
                                disabled={uploadingFabric}
                                type="button"
                              >
                                <X size={11} />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="order-image-empty">
                        <ImageIcon size={28} />
                        <small className="mt-1">Aucune photo</small>
                      </div>
                    )}

                    {canEditFabric && (
                      <div className="text-center mt-2">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => fabricInputRef.current?.click()}
                          disabled={uploadingFabric}
                          type="button"
                        >
                          {uploadingFabric
                            ? <Spinner size="sm" />
                            : <><Upload size={12} className="me-1" /> Ajouter des photos</>}
                        </Button>
                      </div>
                    )}

                    {/* Input fichier caché */}
                    <input
                      ref={fabricInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleUploadFabric}
                      style={{ display: 'none' }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Livraison */}
            <Col md={6}>
              <Card>
                <Card.Header className="py-2 bg-light">
                  <strong className="d-flex align-items-center gap-2">
                    <Calendar size={16} />
                    Livraison
                  </strong>
                </Card.Header>
                <Card.Body>
                  <div>
                    <small className="text-muted">Prévue :</small>{' '}
                    <strong>{customOrderService.formatDate(order.expectedDeliveryDate)}</strong>
                  </div>
                  {order.daysUntilDelivery != null && (
                    <div className={`small mt-1 d-flex align-items-center gap-1 ${
                      order.daysUntilDelivery < 0 ? 'text-danger fw-bold' : 'text-muted'
                    }`}>
                      <Clock size={12} />
                      {customOrderService.formatDaysUntil(order.daysUntilDelivery)}
                    </div>
                  )}
                  {order.actualDeliveryDate && (
                    <div className="mt-2 pt-2 border-top">
                      <small className="text-muted">Livrée le :</small>{' '}
                      <strong className="text-success">
                        {customOrderService.formatDate(order.actualDeliveryDate)}
                      </strong>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Paiement résumé */}
            <Col md={6}>
              <Card>
                <Card.Header className="py-2 bg-light">
                  <strong className="d-flex align-items-center gap-2">
                    <DollarSign size={16} />
                    Paiement
                  </strong>
                </Card.Header>
                <Card.Body>
                  {/* ✅ Bloc remise si applicable */}
                  {order.discountAmount > 0 && (
                    <>
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Sous-total :</small>
                        <span>{customOrderService.formatPrice(order.subtotal)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-success d-flex align-items-center gap-1">
                          <span>🏷️</span>
                          Remise {order.discountType === 'PERCENTAGE'
                            ? `(${order.discountValue}%)`
                            : '(montant fixe)'} :
                        </small>
                        <span className="text-success fw-semibold">
                          -{customOrderService.formatPrice(order.discountAmount)}
                        </span>
                      </div>
                      <hr className="my-1" />
                    </>
                  )}

                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Total :</small>
                    <strong>{customOrderService.formatPrice(order.totalPrice)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Payé :</small>
                    <strong className="text-success">
                      {customOrderService.formatPrice(order.advanceAmount)}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <small className="text-muted">Reste :</small>
                    <strong className={order.isFullyPaid ? 'text-success' : 'text-warning'}>
                      {customOrderService.formatPrice(order.remainingAmount)}
                    </strong>
                  </div>
                  <ProgressBar
                    now={order.paymentPercentage}
                    variant={order.isFullyPaid ? 'success' : 'info'}
                    label={`${order.paymentPercentage}%`}
                    style={{ height: '18px' }}
                  />
                </Card.Body>
              </Card>
            </Col>

            {/* Historique paiements */}
            {order.payments && order.payments.length > 0 && (
              <Col md={12}>
                <Card>
                  <Card.Header className="py-2 bg-light d-flex justify-content-between align-items-center">
                    <strong className="d-flex align-items-center gap-2">
                      <DollarSign size={16} />
                      Historique des paiements ({order.payments.length})
                    </strong>
                  </Card.Header>
                  <ListGroup variant="flush">
                    {order.payments.map(p => (
                      <ListGroup.Item key={p.id} className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="d-flex align-items-center gap-2">
                            <strong>{customOrderService.formatPrice(p.amount)}</strong>
                            <Badge bg={p.paymentType === 'BALANCE' ? 'success' : 'info'} pill>
                              {p.paymentType === 'BALANCE' ? 'Solde' : 'Avance'}
                            </Badge>
                          </div>
                          <small className="text-muted">
                            {p.paymentMethodDisplayName} • {customOrderService.formatDateTime(p.createdAt)}
                            {p.createdByName && ` • ${p.createdByName}`}
                          </small>
                          {p.referenceNumber && (
                            <div className="small text-muted">Réf : {p.referenceNumber}</div>
                          )}
                          {p.notes && (
                            <div className="small fst-italic text-muted">{p.notes}</div>
                          )}
                        </div>
                        {isManager && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeletePayment(p.id)}
                            disabled={loading}
                            title="Supprimer ce paiement"
                          >
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card>
              </Col>
            )}

            {/* Notes */}
            {order.notes && (
              <Col md={12}>
                <Card>
                  <Card.Header className="py-2 bg-light">
                    <strong className="d-flex align-items-center gap-2">
                      <FileText size={16} />
                      Notes
                    </strong>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{order.notes}</div>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </Modal.Body>

        {/* ✅ Footer principal du modal de DÉTAIL avec boutons impression + actions */}
        <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Fermer
          </Button>

          <div className="d-flex gap-2 flex-wrap">

            {/* ✅ Aperçu ticket */}
            <Button
              variant="outline-info"
              size="sm"
              onClick={handlePreviewTicket}
              disabled={loading || printing}
              title="Aperçu du ticket avant impression"
            >
              <Eye size={14} className="me-1" />
              Aperçu
            </Button>

            {/* ✅ Imprimer ticket */}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handlePrintTicket}
              disabled={loading || printing}
              title="Imprimer le bon de commande"
            >
              {printing ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <Printer size={14} className="me-1" />
              )}
              Imprimer
            </Button>

            {/* Ajouter paiement */}
            {canAddPayment && (
              <Button
                variant="success"
                size="sm"
                onClick={() => setShowAddPayment(true)}
                disabled={loading}
              >
                <Plus size={14} className="me-1" />
                Paiement
              </Button>
            )}

            {/* Changer de statut */}
            {nextNormalStatuses.length > 0 && (
              <Dropdown>
                <Dropdown.Toggle variant="primary" size="sm" disabled={loading}>
                  <Edit size={14} className="me-1" />
                  Changer statut
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {nextNormalStatuses.map(status => {
                    const info = customOrderService.getStatusInfo(status);
                    return (
                      <Dropdown.Item
                        key={status}
                        onClick={() => handleChangeStatus(status)}
                      >
                        <span className="me-2">{info.icon}</span>
                        Passer à : <strong>{info.label}</strong>
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* Annuler */}
            {canCancel && isManager && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowCancelConfirm(true)}
                disabled={loading}
              >
                <XCircle size={14} className="me-1" />
                Annuler
              </Button>
            )}
          </div>
        </Modal.Footer>

        {/* Styles pour les images dans le détail */}
        <style>{`
          .order-image-frame {
            position: relative;
            width: 100%;
            height: 130px;
            border-radius: 6px;
            overflow: hidden;
            background: #f8f9fa;
            cursor: zoom-in;
            transition: all 0.2s ease;
            border: 1px solid #e9ecef;
          }
          .order-image-frame:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          }
          .order-image-frame img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .order-image-zoom-hint {
            position: absolute;
            bottom: 6px;
            right: 6px;
            background: rgba(0, 0, 0, 0.6);
            color: white;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.2s ease;
          }
          .order-image-frame:hover .order-image-zoom-hint {
            opacity: 1;
          }
          .order-image-empty {
            width: 100%;
            height: 130px;
            border-radius: 6px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: #adb5bd;
            border: 1px dashed #dee2e6;
          }
        `}</style>
      </Modal>

      {/* Modal ajout paiement */}
      <AddPaymentModal
        show={showAddPayment}
        onHide={() => setShowAddPayment(false)}
        order={order}
        onSuccess={handlePaymentSuccess}
      />

      {/* Modal confirmation annulation */}
      <Modal show={showCancelConfirm} onHide={() => setShowCancelConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer l'annulation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="small">
            Cette action est irréversible. La commande sera marquée comme annulée mais
            conservée dans l'historique.
          </Alert>

          <label className="form-label small fw-semibold">
            Raison de l'annulation <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Ex: Cliente a changé d'avis, problème avec le tissu..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowCancelConfirm(false);
              setCancelReason('');
            }}
            disabled={loading}
          >
            Retour
          </Button>
          <Button
            variant="danger"
            onClick={handleCancel}
            disabled={loading || !cancelReason.trim()}
          >
            {loading ? (
              <><Spinner size="sm" className="me-2" />Annulation...</>
            ) : (
              <>
              Confirmer l'annulation
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal zoom image plein écran */}
      <Modal
        show={!!zoomedImage}
        onHide={() => setZoomedImage(null)}
        size="xl"
        centered
        contentClassName="bg-transparent border-0"
      >
        <Modal.Body className="p-0 text-center position-relative">
          <Button
            variant="light"
            size="sm"
            onClick={() => setZoomedImage(null)}
            className="position-absolute top-0 end-0 m-3"
            style={{ zIndex: 10, borderRadius: '50%', width: 36, height: 36, padding: 0 }}
          >
            <X size={18} />
          </Button>
          {zoomedImage && (
            <>
              <div className="order-zoom-label">
                {zoomedImage.label === 'Modèle' ? (
                  <Shirt size={14} className="me-1" />
                ) : (
                  <Scissors size={14} className="me-1" />
                )}
                {zoomedImage.label}
              </div>
              <img
                src={zoomedImage.url}
                alt={zoomedImage.label}
                style={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  borderRadius: 8,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                }}
              />
            </>
          )}
        </Modal.Body>

        <style>{`
          .order-zoom-label {
            position: absolute;
            top: 16px;
            left: 16px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            z-index: 10;
          }
        `}</style>
      </Modal>
    </>
  );
};

export default OrderDetailModal;